import React, { useState } from 'react';
import { Trash2, Plus, X, Upload, ZoomIn, Loader2, Info } from 'lucide-react';
import { getUploadUrlWithQuota } from '../../services/uploadWithQuota';
import ColorSwatchPicker from './ColorSwatchPicker';

interface GalleryImage {
  id: string;
  url: string;
  alt: string;
}

interface GalleryBlockData {
  images: GalleryImage[];
  layout: 'grid' | 'slider';
  accentColor?: string;
}

const DEFAULT_ACCENT_COLOR = '#f97316';

interface GalleryBlockProps {
  data: GalleryBlockData;
  onChange: (data: GalleryBlockData) => void;
  onDelete: () => void;
  isPreview?: boolean;
}

const GalleryBlock: React.FC<GalleryBlockProps> = ({ data, onChange, onDelete, isPreview }) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadingImageId, setUploadingImageId] = useState<string | null>(null);

  const addImage = () => {
    const newImage: GalleryImage = {
      id: Date.now().toString(),
      url: '',
      alt: ''
    };
    onChange({ ...data, images: [...data.images, newImage] });
  };

  const updateImage = (id: string, field: keyof GalleryImage, value: string) => {
    const updatedImages = data.images.map(img =>
      img.id === id ? { ...img, [field]: value } : img
    );
    onChange({ ...data, images: updatedImages });
  };

  const handleImageUpload = async (id: string, file: File) => {
    try {
      setUploadingImageId(id);
      setUploading(true);
      
      // Upload using Cloudflare Worker
      const result = await getUploadUrlWithQuota(
        file.name,
        file.type,
        file.size,
        file,
        (prog, msg) => {
          setProgress(prog);
          console.log(msg);
        }
      );
      
      updateImage(id, 'url', result.publicUrl);
    } catch (error) {
      console.error('Upload failed:', error);
      alert('อัพโหลดรูปภาพล้มเหลว กรุณาลองใหม่อีกครั้ง');
    } finally {
      setUploadingImageId(null);
      setUploading(false);
      setProgress(0);
    }
  };

  const deleteImage = (id: string) => {
    onChange({ ...data, images: data.images.filter(img => img.id !== id) });
  };

  const accentColor = data.accentColor || DEFAULT_ACCENT_COLOR;

  if (isPreview) {
    if (data.layout === 'grid') {
      return (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {data.images.map((image) => (
              <div
                key={image.id}
                className="relative aspect-square rounded-2xl overflow-hidden cursor-pointer group shadow-lg hover:shadow-2xl transition-all duration-300"
                onClick={() => setSelectedImage(image.url)}
              >
                <img
                  src={image.url}
                  alt={image.alt}
                  className="w-full h-full object-cover transition-all duration-500 group-hover:scale-125 group-hover:rotate-2"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center">
                  <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                    <ZoomIn className="w-10 h-10 text-white drop-shadow-lg" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {selectedImage && (
            <div
              className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedImage(null)}
            >
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 text-white hover:text-gray-300"
              >
                <X className="w-8 h-8" />
              </button>
              <img
                src={selectedImage}
                alt="Preview"
                className="max-w-full max-h-full object-contain"
              />
            </div>
          )}
        </>
      );
    } else {
      return (
        <div className="relative">
          <div className="overflow-hidden rounded-lg">
            {data.images[currentSlide] && (
              <img
                src={data.images[currentSlide].url}
                alt={data.images[currentSlide].alt}
                className="w-full aspect-video object-cover"
              />
            )}
          </div>
          
          {data.images.length > 1 && (
            <>
              <button
                onClick={() => setCurrentSlide((prev) => (prev - 1 + data.images.length) % data.images.length)}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow-lg"
              >
                ‹
              </button>
              <button
                onClick={() => setCurrentSlide((prev) => (prev + 1) % data.images.length)}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow-lg"
              >
                ›
              </button>
              <div className="flex justify-center gap-2 mt-4">
                {data.images.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-2 h-2 rounded-full ${index === currentSlide ? '' : 'bg-gray-300'}`}
                    style={index === currentSlide ? { backgroundColor: accentColor } : undefined}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      );
    }
  }

  return (
    <div className="border border-gray-200 rounded-lg p-6 bg-white">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-gray-900">แกลเลอรี่รูปภาพ</h3>
          <div className="relative">
            <Info className="w-4 h-4 text-gray-400 cursor-help peer" />
            <div className="absolute left-0 top-6 w-64 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 invisible peer-hover:opacity-100 peer-hover:visible transition-all duration-200 z-10 shadow-xl pointer-events-none">
              <p className="font-semibold mb-1">แสดงรูปภาพหลายรูป</p>
              <p className="text-gray-300">สร้างแกลเลอรี่รูปภาพสินค้าหรือผลงาน ให้ลูกค้าเห็นรายละเอียดจากหลายมุมมอง</p>
            </div>
          </div>
        </div>
        <button
          onClick={onDelete}
          className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-colors"
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">รูปแบบการแสดงผล</label>
          <div className="flex items-center gap-2">
            {data.layout === 'slider' && (
              <ColorSwatchPicker
                compact
                color={accentColor}
                fallback={DEFAULT_ACCENT_COLOR}
                onChange={(hex) => onChange({ ...data, accentColor: hex })}
              />
            )}
            <select
              value={data.layout}
              onChange={(e) => onChange({ ...data, layout: e.target.value as 'grid' | 'slider' })}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="grid">Grid (ตาราง)</option>
              <option value="slider">Slider (สไลด์)</option>
            </select>
          </div>
        </div>

        {/* Modern Grid Layout */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {data.images.map((image) => (
            <div key={image.id} className="relative group">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    handleImageUpload(image.id, file);
                    e.target.value = '';
                  }
                }}
                disabled={uploadingImageId === image.id}
                className="hidden"
                id={`upload-${image.id}`}
              />
              
              {image.url ? (
                <div className="relative aspect-square rounded-xl overflow-hidden border-2 border-gray-200 hover:border-orange-400 transition-all">
                  <img
                    src={image.url}
                    alt={image.alt}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center gap-2">
                    <label
                      htmlFor={`upload-${image.id}`}
                      className="opacity-0 group-hover:opacity-100 transition-all cursor-pointer bg-white text-orange-600 p-2 rounded-lg hover:bg-orange-50"
                    >
                      <Upload className="w-5 h-5" />
                    </label>
                    <button
                      onClick={() => deleteImage(image.id)}
                      className="opacity-0 group-hover:opacity-100 transition-all bg-white text-red-600 p-2 rounded-lg hover:bg-red-50"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ) : (
                <label
                  htmlFor={`upload-${image.id}`}
                  className={`aspect-square rounded-xl border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-all ${
                    uploadingImageId === image.id
                      ? 'border-orange-400 bg-orange-50'
                      : 'border-gray-300 hover:border-orange-500 hover:bg-orange-50'
                  }`}
                >
                  {uploadingImageId === image.id ? (
                    <div className="text-center">
                      <Loader2 className="w-10 h-10 animate-spin text-orange-500 mx-auto mb-2" />
                      <p className="text-xs text-orange-600 font-medium">{progress}%</p>
                    </div>
                  ) : (
                    <div className="text-center">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-2">
                        <Plus className="w-6 h-6 text-white" />
                      </div>
                      <p className="text-xs text-gray-600 font-medium">อัพโหลด</p>
                    </div>
                  )}
                </label>
              )}
            </div>
          ))}

          {/* Add New Image Button */}
          <button
            onClick={addImage}
            className="aspect-square rounded-xl border-2 border-dashed border-gray-300 hover:border-orange-500 hover:bg-orange-50 transition-all flex flex-col items-center justify-center group"
          >
            <div className="w-12 h-12 bg-gray-200 group-hover:bg-orange-500 rounded-full flex items-center justify-center mb-2 transition-all">
              <Plus className="w-6 h-6 text-gray-600 group-hover:text-white transition-all" />
            </div>
            <p className="text-xs text-gray-600 group-hover:text-orange-600 font-medium transition-all">เพิ่มรูป</p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default GalleryBlock;
